import { type ServerService } from '@lib/services/server-data'
import {
  BrowserGroup,
  type ClientCapabilities,
  DesktopOsGroup,
  DeviceType,
  MobileOsGroup,
} from '@lib/utils/client-capabilities'
import { isTouchDevice } from '@lib/utils/is-touch'
import { lowerCaseValues } from '@lib/utils/object'

import { capitalize, capitalizeEachWord } from '../../utils/strings'

export class BrowserService {
  private readonly info: ClientCapabilities | null

  constructor(shWebServerService: ServerService) {
    const { clientCapabilities } = shWebServerService.get()
    this.info = clientCapabilities ? lowerCaseValues(clientCapabilities) : null
  }

  private getOrFalse(fn: (info: ClientCapabilities) => boolean) {
    return this.getOrDefault(fn, false)
  }

  private getOrDefault<T, D>(fn: (info: ClientCapabilities) => T, def: D): T | D {
    if (this.info) {
      return fn(this.info)
    }
    return def
  }

  // GSF-1842 -- Mac OS Version string. Temporary field to support Llama for Mac experiment. Do not use!
  get macOsVersion(): string | undefined {
    return this.getOrDefault(info => info.os?.split(';')[0], '')
  }

  get windowsOsVersion(): string | undefined {
    if (!this.isWindows) {
      return
    }

    return this.getOrDefault(info => info.os.split(' ')[1], '')
  }

  get isWindows(): boolean {
    return this.getOrFalse(info => info.os.includes(DesktopOsGroup.Windows))
  }

  get isMac(): boolean {
    return this.getOrFalse(info => info.os.includes(DesktopOsGroup.Mac)) && !this.isIOS
  }

  get isIOS(): boolean {
    return this.getOrFalse(info => info.osGroup.includes(MobileOsGroup.iOS))
  }

  /**
   * Warning!
   * This iPad detection is inconsistent between server and client-side
   */
  get isIPad(): boolean {
    return (this.isIOS && this.isTablet) || this.isIPadOS13
  }

  /**
   * iPadOS13 pretends to be an Intel-based Mac OS device with Safari by default
   * more details: https://grammarly.atlassian.net/wiki/spaces/GEN/pages/1168539715/iPadOS13+support+in+Funnel+Facade+June+17+2020
   * Important! This works ONLY at the FE-side
   */
  get isIPadOS13(): boolean {
    return this.isMac && isTouchDevice()
  }

  get isAndroid(): boolean {
    return this.getOrFalse(info => info.osGroup.includes(MobileOsGroup.Android))
  }

  get deviceType(): DeviceType {
    if (this.isIPadOS13) {
      return DeviceType.Tablet
    }
    return this.getOrDefault(info => info.deviceType, DeviceType.Unknown)
  }

  get isMobile(): boolean {
    return this.getOrFalse(info => info.deviceType === DeviceType.Mobile)
  }

  get isMobileAndroidOrIOS(): boolean {
    return this.isIOS || this.isAndroid
  }

  get isTablet(): boolean {
    return this.getOrFalse(info => info.deviceType === DeviceType.Tablet)
  }

  get isMobileOrTablet(): boolean {
    return this.isMobile || this.isTablet
  }

  get isChrome(): boolean {
    return this.getOrFalse(info => info.browserGroup === BrowserGroup.Chrome)
  }

  get isFirefox(): boolean {
    return this.getOrFalse(info => info.browserGroup === BrowserGroup.Firefox)
  }

  get isSafari(): boolean {
    return this.getOrFalse(info => info.browserGroup === BrowserGroup.Safari)
  }

  get isIE(): boolean {
    return this.getOrFalse(info => info.browserGroup === BrowserGroup.IE)
  }

  get isEdge(): boolean {
    return this.getOrFalse(info => info.browserGroup === BrowserGroup.Edge)
  }

  get isEdgeChromium(): boolean {
    return this.isEdge && this.majorVersion >= 79
  }

  get operatingSystem(): string {
    return this.getOrDefault(i => i.os, '')
  }

  get operatingSystemDisplayName(): string {
    return this.getOrDefault(i => (i.os === MobileOsGroup.iOS ? 'iOS' : capitalize(i.os) || ''), '')
  }

  get browserDisplayName(): string {
    return capitalizeEachWord(this.browserName)
  }

  get browserName(): string {
    return this.getOrDefault(i => i.browser || '', '')
  }

  get majorVersion(): number {
    return this.getOrDefault(info => info.majorVersion, 0)
  }
}
